// Lógica pura de la Configuración de autovalidación por kms (sin React), para
// aislar el mapeo API<->formulario y el esquema de validación. Testeable el día
// que el repo adopte Vitest; hoy solo pasa por lint/build (ver plan).
//
// Contrato backend PROPUESTO (GET/PUT
// /api/v1/taxi/platform/service-validation/autovalidation-config):
//   { kms_default_cap: <entero km>, kms_tolerance_pct: <fracción 0..1> }
// La UI trabaja la tolerancia como PORCENTAJE entero (10 = 10%) y convierte a
// fracción (0.10) al enviar; al leer, convierte fracción -> porcentaje.
import * as Yup from "yup"

// Valores por defecto de negocio: servicios cortos con tope de 50 km,
// tolerancia de 10% de diferencia entre kms registrados y saneados, y
// tolerancia de desviación de dirección de 100 m (par escala creación vs
// escala registrada por el operador en la app móvil).
export const DEFAULT_AUTOVAL_CONFIG = {
  kms_default_cap: 50,
  kms_tolerance_pct: 0.10,
  address_deviation_tolerance_m: 100,
  // Empresas (partner_id como string) excluidas de la regla de monto>0 en la
  // aprobación de PODs. Default: ninguna.
  pods_approval_excluded_company_ids: [],
}

// API (fracción) -> formulario (porcentaje entero). Aplica defaults si falta
// algún campo. La tolerancia 0.10 se muestra como 10. La tolerancia de
// desviación se trabaja como entero de metros.
export const toForm = (apiCfg) => {
  const cfg = apiCfg || {}
  const cap = Number(cfg.kms_default_cap)
  const pct = Number(cfg.kms_tolerance_pct)
  const dev = Number(cfg.address_deviation_tolerance_m)
  return {
    kms_default_cap: Number.isFinite(cap) ? cap : DEFAULT_AUTOVAL_CONFIG.kms_default_cap,
    kms_tolerance_pct: Number.isFinite(pct)
      ? Math.round(pct * 100 * 100) / 100
      : DEFAULT_AUTOVAL_CONFIG.kms_tolerance_pct * 100,
    address_deviation_tolerance_m: Number.isFinite(dev) && dev > 0
      ? Math.round(dev)
      : DEFAULT_AUTOVAL_CONFIG.address_deviation_tolerance_m,
    // Lista de empresas excluidas: siempre array de strings (vacío si falta).
    pods_approval_excluded_company_ids: Array.isArray(cfg.pods_approval_excluded_company_ids)
      ? cfg.pods_approval_excluded_company_ids.map((v) => String(v))
      : [],
  }
}

// Formulario (porcentaje) -> API (fracción). El tope se envía como entero de km,
// la tolerancia como fracción (10 -> 0.10) y la tolerancia de desviación como
// entero de metros. `toApi` SIEMPRE envía los tres campos.
export const toApi = (formValues) => {
  const values = formValues || {}
  const cap = Number(values.kms_default_cap)
  const pct = Number(values.kms_tolerance_pct)
  const dev = Number(values.address_deviation_tolerance_m)
  return {
    kms_default_cap: Number.isFinite(cap) ? Math.round(cap) : DEFAULT_AUTOVAL_CONFIG.kms_default_cap,
    kms_tolerance_pct: Number.isFinite(pct)
      ? Math.round((pct / 100) * 10000) / 10000
      : DEFAULT_AUTOVAL_CONFIG.kms_tolerance_pct,
    address_deviation_tolerance_m: Number.isFinite(dev) && dev > 0
      ? Math.round(dev)
      : DEFAULT_AUTOVAL_CONFIG.address_deviation_tolerance_m,
    // Empresas excluidas: normalizadas a strings (array vacío si falta).
    pods_approval_excluded_company_ids: Array.isArray(values.pods_approval_excluded_company_ids)
      ? values.pods_approval_excluded_company_ids.map((v) => String(v))
      : [],
  }
}

// Esquema de validación yup del formulario (valores en unidades de la UI):
//  - kms_default_cap: requerido, número, entero, > 0
//  - kms_tolerance_pct (porcentaje): requerido, número, 0..100
export const buildAutovalSchema = (t) => {
  const tr = typeof t === "function" ? t : (k) => k
  return Yup.object({
    kms_default_cap: Yup.number()
      .typeError(tr("AutovalCapError") || "Ingresa un número de kms válido")
      .required(tr("AutovalCapError") || "Ingresa un número de kms válido")
      .integer(tr("AutovalCapError") || "El tope de kms debe ser un entero")
      .positive(tr("AutovalCapError") || "El tope de kms debe ser mayor que 0"),
    kms_tolerance_pct: Yup.number()
      .typeError(tr("AutovalPctError") || "Ingresa un porcentaje entre 0 y 100")
      .required(tr("AutovalPctError") || "Ingresa un porcentaje entre 0 y 100")
      .min(0, tr("AutovalPctError") || "El porcentaje no puede ser menor que 0")
      .max(100, tr("AutovalPctError") || "El porcentaje no puede ser mayor que 100"),
    address_deviation_tolerance_m: Yup.number()
      .typeError(tr("AddressDeviationToleranceError") || "Ingresa una tolerancia válida en metros")
      .required(tr("AddressDeviationToleranceError") || "Ingresa una tolerancia válida en metros")
      .integer(tr("AddressDeviationToleranceError") || "La tolerancia debe ser un entero de metros")
      .positive(tr("AddressDeviationToleranceError") || "La tolerancia debe ser mayor que 0")
      .max(100000, tr("AddressDeviationToleranceError") || "La tolerancia no puede exceder 100000 m"),
    // Empresas excluidas de la aprobación de PODs: opcional, array de strings.
    pods_approval_excluded_company_ids: Yup.array()
      .of(Yup.string())
      .optional(),
  })
}
